import { Container, Grid, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolInstance, isInstance } from '@synergy/config';
import { PoolTypography } from '@synergy/components-next';
import { ReactElement, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';

import { CategoryBar } from '../../searchpage/category-bar/category-bar';
import { PoolPageHeader } from '../../../components/page-header/page-header';
import { SupplierDirectoryRow } from './supplier-directory-row';
import { SupplierDirectorySearchBar } from './supplier-directory-search-bar';
import { fetchSupplierDirectory, supplierDirectoryReset } from '../../../store/supplier-actions';
import { supplierDirectoryCategorySelector, supplierDirectoryDataSelector } from '../../../store/supplier-selectors';
import { useAppDispatch, useAppSelector } from '../../../../store/store';

export const SupplierDirectory = (): ReactElement => {
    const dispatch = useAppDispatch();
    const { items, hasMore, nextPage } = useAppSelector(supplierDirectoryDataSelector);
    const categorySelector = useAppSelector(supplierDirectoryCategorySelector);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        searchParams.set('page', '1');
        dispatch(fetchSupplierDirectory(searchParams));
    }, [dispatch, searchParams]);

    useEffect(() => {
        return () => {
            dispatch(supplierDirectoryReset());
        };
    }, [dispatch]);

    const handleLoadMore = (): void => {
        if (hasMore) {
            searchParams.set('page', nextPage.toString());
            dispatch(fetchSupplierDirectory(searchParams));
        }
    };

    return (
        <>
            <PoolPageHeader variant="grey">
                <FormattedMessage id="app.supplierDirectory.title" />
            </PoolPageHeader>

            <SupplierDirectorySearchBar />

            {isInstance(PoolInstance.CH) && (
                <CategoryBar categorySelector={categorySelector} showBackButtonWithSearch={false} />
            )}

            <Container maxWidth="xl" sx={{ pt: 2, pb: 2.5 }}>
                <InfiniteScroll pageStart={1} loadMore={handleLoadMore} hasMore={hasMore}>
                    <Grid container={true} spacing={2}>
                        {items.map((item, index) => (
                            <SupplierDirectoryRow key={index} item={item} />
                        ))}
                    </Grid>
                    {items.length === 0 && (
                        <Stack sx={{ mt: 5 }} gap={1.5}>
                            <PoolTypography variant="h4" translationId="app.supplierDirectory.empty" />
                            <FormattedMessage id="app.supplierDirectory.empty.description" />
                        </Stack>
                    )}
                </InfiniteScroll>
            </Container>
        </>
    );
};
